import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, CloudUpload } from '@mui/icons-material';
import { dateFormat } from '../../utils';
import * as subMatFolderActions from '../../store/subMatFolder';
import SubMatFolderExplorerChrome from './SubMatFolderExplorerChrome';
import SubMatFolderIcon, { AdditionalDocBadge } from './SubMatFolderIcon';
import {
  formatSubMatEndDate,
  formatSubMatExpiryStatusText,
  getSubMatExpirySortValue,
  getSubMatExpiryStatusSortValue,
  getSubMatExpiryStatusTextColor,
} from './SubMatFolderExpiryStatus';
import {
  ColumnDataCell,
  ColumnHeaderCell,
  ColumnRow,
  useColumnWidths,
} from './SubMatFolderColumnLayout';

const PDF_ACCEPT = '.pdf,application/pdf';

export const SubMatWorkProgressOverlay = ({ progress }) => {
  if (!progress?.total) return null;
  const percent = Math.round((progress.done / progress.total) * 100);
  const detail = progress.name ? ` · ${progress.name}` : '';
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 2,
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.84),
        pointerEvents: 'auto',
      }}
    >
      <Typography variant="body2" noWrap title={`${progress.verb} 중 ${progress.done}/${progress.total}${detail}`} sx={{ maxWidth: '100%' }}>
        {`${progress.verb} 중 ${progress.done}/${progress.total}${detail}`}
      </Typography>
      <LinearProgress variant="determinate" value={percent} sx={{ width: '100%', maxWidth: 280 }} />
    </Box>
  );
};

const EXPLORER_ROW_PX = 32;
const MARQUEE_THRESHOLD_PX = 4;

/** 트리 TreeItem selected와 동일 — primary 반투명 */
const selectionBg = (theme) => alpha(
  theme.palette.primary.main,
  theme.palette.action.selectedOpacity,
);
const selectionBgStrong = (theme) => alpha(
  theme.palette.primary.main,
  theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity,
);

const entryKeyOf = (entry) => `${entry.kind}-${entry.id}`;

const rectsIntersect = (a, b) => !(
  a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom
);

/** 이름 | 수정한 날짜 | 만료일 | 상태 | 유형 | 크기 */
const EXPLORER_COL_MIN = [90, 80, 88, 64, 48, 72];
const EXPLORER_COL_DEFAULT = [180, 124, 100, 88, 64, 88];

/** TEST 루트: … | 시험성과대비표 */
const EXPLORER_COL_MIN_CMP = [80, 72, 80, 56, 44, 72, 80];
const EXPLORER_COL_DEFAULT_CMP = [150, 112, 96, 80, 56, 88, 100];

/** 윈도우 탐색기: 컬럼 첫 클릭 시 기본 방향 (날짜=최신순) */
const SORT_FIRST_DIR = {
  name: 'asc',
  modified: 'desc',
  expiry: 'asc',
  expiryStatus: 'asc',
  type: 'asc',
  size: 'asc',
  comparison: 'asc',
};

const compareNameKo = (a, b, dir = 'asc') => {
  const mul = dir === 'asc' ? 1 : -1;
  return mul * (a.name || '').localeCompare(b.name || '', 'ko', {
    numeric: true,
    sensitivity: 'base',
  });
};

const compareByKey = (a, b, key, dir) => {
  const mul = dir === 'asc' ? 1 : -1;
  let c = 0;
  switch (key) {
    case 'name':
      return compareNameKo(a, b, dir);
    case 'modified': {
      const ta = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
      const tb = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
      c = ta - tb;
      break;
    }
    case 'expiry': {
      const ea = a.kind === 'file' ? getSubMatExpirySortValue(a.file) : Number.NEGATIVE_INFINITY;
      const eb = b.kind === 'file' ? getSubMatExpirySortValue(b.file) : Number.NEGATIVE_INFINITY;
      c = ea - eb;
      break;
    }
    case 'expiryStatus': {
      const ea = a.kind === 'file' ? getSubMatExpiryStatusSortValue(a.file) : -1;
      const eb = b.kind === 'file' ? getSubMatExpiryStatusSortValue(b.file) : -1;
      c = ea - eb;
      break;
    }
    case 'type':
      c = (a.typeLabel || '').localeCompare(b.typeLabel || '', 'ko');
      break;
    case 'size':
      c = (a.sizeBytes || 0) - (b.sizeBytes || 0);
      break;
    case 'comparison':
      c = (a.hasComparison ? 1 : 0) - (b.hasComparison ? 1 : 0);
      break;
    default:
      c = 0;
  }
  if (c === 0) {
    return compareNameKo(a, b, 'asc');
  }
  return c * mul;
};

/**
 * 윈도우 탐색기 Details 정렬에 맞춤.
 * - 크기 ASC: 폴더(가나다) → 파일(작은→큰)
 * - 크기 DESC: 파일(큰→작은) → 폴더(가나다)
 * - 유형 ASC/DESC: 폴더(이름 방향) → 파일(유형·이름 방향) — 폴더는 항상 위
 * - 이름/날짜/대비표: 폴더 위 → 파일, 각 그룹을 컬럼·방향으로 정렬
 */
const sortExplorerEntries = (entries, key, dir) => {
  const folders = entries.filter((e) => e.kind === 'folder');
  const files = entries.filter((e) => e.kind === 'file');

  if (key === 'size') {
    // 폴더는 크기 없음 → 가나다 고정, DESC일 때만 파일 그룹이 위로
    folders.sort((a, b) => compareNameKo(a, b, 'asc'));
    files.sort((a, b) => compareByKey(a, b, 'size', dir));
    return dir === 'asc' ? [...folders, ...files] : [...files, ...folders];
  }

  if (key === 'type') {
    folders.sort((a, b) => compareNameKo(a, b, dir));
    files.sort((a, b) => {
      const mul = dir === 'asc' ? 1 : -1;
      let c = mul * (a.typeLabel || '').localeCompare(b.typeLabel || '', 'ko');
      if (c === 0) c = compareNameKo(a, b, dir);
      return c;
    });
    return [...folders, ...files];
  }

  folders.sort((a, b) => compareByKey(a, b, key, dir));
  files.sort((a, b) => compareByKey(a, b, key, dir));
  return [...folders, ...files];
};

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === '') return '';
  const n = Number(bytes);
  if (Number.isNaN(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatFileType = (file) => {
  if (!file) return '';
  const ct = (file.contentType || '').toLowerCase();
  if (ct.includes('pdf')) return 'PDF 파일';
  const name = file.fileName || '';
  const ext = name.includes('.') ? name.split('.').pop().toUpperCase() : '';
  return ext ? `${ext} 파일` : '파일';
};

const ExplorerHeader = ({
  widths,
  startResize,
  onAutoFit,
  showComparison,
  sortKey,
  sortDir,
  onSort,
}) => {
  const sortProps = (key) => ({
    sortable: true,
    sortActive: sortKey === key,
    sortDir,
    onSort: () => onSort?.(key),
  });
  return (
    <ColumnRow
      header
      sticky={false}
      fitContent
      gridTemplateColumns={widths.map((w) => `${w}px`).join(' ')}
    >
      <ColumnHeaderCell
        label="이름"
        resizable
        fitIndex={0}
        onResizeStart={(x) => startResize(0, x)}
        onAutoFit={() => onAutoFit?.(0)}
        {...sortProps('name')}
      />
      <ColumnHeaderCell
        label="수정한 날짜"
        resizable
        fitIndex={1}
        onResizeStart={(x) => startResize(1, x)}
        onAutoFit={() => onAutoFit?.(1)}
        {...sortProps('modified')}
      />
      <ColumnHeaderCell
        label="만료일"
        resizable
        fitIndex={2}
        onResizeStart={(x) => startResize(2, x)}
        onAutoFit={() => onAutoFit?.(2)}
        {...sortProps('expiry')}
      />
      <ColumnHeaderCell
        label="상태"
        resizable
        fitIndex={3}
        onResizeStart={(x) => startResize(3, x)}
        onAutoFit={() => onAutoFit?.(3)}
        {...sortProps('expiryStatus')}
      />
      <ColumnHeaderCell
        label="유형"
        resizable
        fitIndex={4}
        onResizeStart={(x) => startResize(4, x)}
        onAutoFit={() => onAutoFit?.(4)}
        {...sortProps('type')}
      />
      <ColumnHeaderCell
        label="크기"
        align="right"
        resizable
        fitIndex={5}
        onResizeStart={(x) => startResize(5, x)}
        onAutoFit={() => onAutoFit?.(5)}
        {...sortProps('size')}
      />
      {showComparison && (
        <ColumnHeaderCell
          label="시험성과대비표"
          align="center"
          resizable
          isLast
          fitIndex={6}
          onResizeStart={(x) => startResize(6, x)}
          onAutoFit={() => onAutoFit?.(6)}
          {...sortProps('comparison')}
        />
      )}
    </ColumnRow>
  );
};

const HighlightedName = ({ text, pattern }) => {
  const chunks = subMatFolderActions.splitWildcardHighlights(text, pattern);
  return (
    <>
      {chunks.map((chunk, i) => (
        chunk.match ? (
          <Box
            key={`h-${i}`}
            component="mark"
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
              color: 'inherit',
              font: 'inherit',
              px: '1px',
              mx: '-1px',
              borderRadius: '2px',
            }}
          >
            {chunk.text}
          </Box>
        ) : (
          <React.Fragment key={`t-${i}`}>{chunk.text}</React.Fragment>
        )
      ))}
    </>
  );
};

const ExplorerRow = ({
  widths,
  icon,
  primary,
  endAdornment = null,
  modified,
  expiryDateLabel,
  expiryStatusLabel,
  expiryStatusColor,
  typeLabel,
  sizeLabel,
  comparisonSlot,
  showComparison,
  title,
  selected,
  plain = false,
  dropTarget = false,
  dropFolderId,
  entryKey,
  draggable = false,
  cut = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onClick,
  onDoubleClick,
  onContextMenu,
}) => (
  <ColumnRow
    data-submat-ctx=""
    data-explorer-row={entryKey || undefined}
    fitContent
    {...(dropFolderId ? { [subMatFolderActions.DROP_FOLDER_ATTR]: dropFolderId } : {})}
    gridTemplateColumns={widths.map((w) => `${w}px`).join(' ')}
    draggable={draggable}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    onClick={onClick}
    onDoubleClick={onDoubleClick}
    onContextMenu={onContextMenu}
    title={title}
    sx={{
      py: 0,
      mt: '2px',
      minHeight: EXPLORER_ROW_PX,
      height: EXPLORER_ROW_PX,
      boxSizing: 'border-box',
      cursor: draggable ? 'grab' : 'pointer',
      border: '1px solid',
      borderColor: (theme) => (selected ? theme.palette.primary.main : 'transparent'),
      borderRadius: '4px',
      bgcolor: (theme) => {
        if (dropTarget || selected) return selectionBg(theme);
        return 'background.paper';
      },
      outline: 'none',
      opacity: cut ? 0.4 : 1,
      transition: 'opacity 0.12s ease',
      '&:hover': {
        bgcolor: (theme) => {
          if (dropTarget || selected) return selectionBgStrong(theme);
          return 'action.hover';
        },
      },
      fontSize: '0.9375rem',
      lineHeight: 1.45,
    }}
  >
    <Box
      data-explorer-fit="0"
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        px: 1,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        overflow: 'hidden',
      }}
    >
      <Box component="span" sx={{ flexShrink: 0, lineHeight: 1.45, fontSize: '1.05em' }}>{icon}</Box>
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {primary}
        </Box>
        {endAdornment}
      </Box>
    </Box>
    <ColumnDataCell data-explorer-fit="1">{modified || ''}</ColumnDataCell>
    <ColumnDataCell data-explorer-fit="2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {expiryDateLabel || ''}
    </ColumnDataCell>
    <ColumnDataCell data-explorer-fit="3" sx={expiryStatusColor ? { color: expiryStatusColor } : undefined}>
      {expiryStatusLabel || ''}
    </ColumnDataCell>
    <ColumnDataCell data-explorer-fit="4">{typeLabel || ''}</ColumnDataCell>
    <ColumnDataCell data-explorer-fit="5" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {sizeLabel || ''}
    </ColumnDataCell>
    {showComparison && (
      <ColumnDataCell data-explorer-fit="6" align="center" onClick={(e) => e.stopPropagation()}>
        {comparisonSlot || null}
      </ColumnDataCell>
    )}
  </ColumnRow>
);

const ComparisonViewButton = ({ file, onPreviewFile, onError }) => {
  const cmp = file?.comparisonFile;
  if (!cmp?.documentPath) return null;
  return (
    <Button
      size="small"
      variant="text"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await onPreviewFile?.(cmp);
        } catch (err) {
          onError?.(err.message || '시험성과대비표를 열 수 없습니다.');
        }
      }}
      sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.75rem' }}
    >
      보기
    </Button>
  );
};

const SubMatFolderExplorerPane = ({
  folder,
  folderMap,
  folders,
  allFolders,
  links = [],
  files,
  filesFolderId,
  selectedFileId,
  uploading = false,
  activityLabel = '',
  chrome = true,
  searchQuery: controlledSearch,
  onSearchQueryChange,
  onNavigate,
  onCreateFolder,
  onDeleteFolder,
  onDeleteSelection,
  onSelectionChange,
  onUploadFile,
  onSelectFile,
  onContextMenu,
  onPreviewFile,
  onError,
  onFolderDrop,
  cutFolderIds = [],
  selectedFolderIdsRef = null,
  selectedFileInfosRef = null,
  externalDropTargetId = null,
  canGoBack = false,
  canGoForward = false,
  canGoUp = false,
  onGoBack,
  onGoForward,
  onGoUp,
  onRefresh,
  specOnly = false,
  onSpecOnlyChange,
  onOpenAdditional,
  busy = false,
  workProgress = null,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [folderDropTargetId, setFolderDropTargetId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [anchorKey, setAnchorKey] = useState(null);
  const [marquee, setMarquee] = useState(null); // { left, top, width, height }
  const [internalSearch, setInternalSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const marqueeRef = useRef(null);
  const paneFocusRef = useRef(null);
  const suppressClickRef = useRef(false);
  const selectedKeysRef = useRef(selectedKeys);
  selectedKeysRef.current = selectedKeys;

  const focusPane = () => {
    paneFocusRef.current?.focus?.({ preventScroll: true });
  };

  const showComparison = folder?.rootKind === 'TEST' || folder?.rootKind === 'ETC';
  const colDefaults = showComparison ? EXPLORER_COL_DEFAULT_CMP : EXPLORER_COL_DEFAULT;
  const colMins = showComparison ? EXPLORER_COL_MIN_CMP : EXPLORER_COL_MIN;
  const { widths, startResize, cancelResize, setColumnWidth } = useColumnWidths(colDefaults, colMins);

  const isSearchControlled = controlledSearch !== undefined;
  const searchQuery = isSearchControlled ? controlledSearch : internalSearch;
  const setSearchQuery = (v) => {
    if (isSearchControlled) onSearchQueryChange?.(v);
    else setInternalSearch(v);
  };

  const fitColumnToContent = (index) => {
    cancelResize();
    const root = paneFocusRef.current;
    if (!root) return;
    const nodes = root.querySelectorAll(`[data-explorer-fit="${index}"]`);
    let max = 0;
    nodes.forEach((el) => {
      const clone = el.cloneNode(true);
      const loosen = (node) => {
        node.style.width = 'max-content';
        node.style.maxWidth = 'none';
        node.style.minWidth = 'max-content';
        node.style.whiteSpace = 'nowrap';
        node.style.overflow = 'visible';
        node.style.textOverflow = 'clip';
        node.style.flex = '0 0 auto';
      };
      loosen(clone);
      clone.querySelectorAll('*').forEach((node) => loosen(node));
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      clone.style.visibility = 'hidden';
      clone.style.pointerEvents = 'none';
      document.body.appendChild(clone);
      max = Math.max(max, clone.getBoundingClientRect().width);
      clone.remove();
    });
    if (max > 0) setColumnWidth(index, max + 2);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(SORT_FIRST_DIR[key] || 'asc');
    }
  };

  const uploadable = subMatFolderActions.isUploadableFolder(folder);
  const showsFile = uploadable || Boolean(folder?.linkView);
  const children = folder?.linkView
    ? []
    : (folder
      ? subMatFolderActions.childrenWithLinks(folders, allFolders || folders, links, folder.id)
      : []);
  const filesKey = folder?.linkView ? folder.sourceFolderId : folder?.id;
  const folderFiles = filesFolderId === filesKey ? files : [];
  const currentFile = folder?.currentFile || folderFiles.find((f) => f.isCurrent);

  const canAddChild = subMatFolderActions.canAddChildFolder(folder, folders);

  const isSearching = Boolean(searchQuery.trim());
  const searchResults = useMemo(() => {
    if (!isSearching || !folder) return [];
    return subMatFolderActions.searchInSubtree(
      folders,
      folderMap,
      folder.id,
      searchQuery,
      currentFile,
    );
  }, [isSearching, folder, folders, folderMap, searchQuery, currentFile]);

  const sortedFolderEntries = useMemo(() => {
    const entries = children.map((child) => ({
      kind: 'folder',
      id: child.id,
      name: child.name || '',
      modifiedAt: child.updatedAt || child.createdAt || null,
      typeLabel: '파일 폴더',
      sizeBytes: 0,
      hasComparison: false,
      folder: child,
    }));
    if (showsFile && currentFile) {
      entries.push({
        kind: 'file',
        id: currentFile.id,
        name: currentFile.fileName || '',
        modifiedAt: currentFile.uploadedAt || null,
        typeLabel: formatFileType(currentFile),
        sizeBytes: Number(currentFile.size) || 0,
        hasComparison: Boolean(currentFile.comparisonFile?.documentPath),
        file: currentFile,
      });
    }
    return sortExplorerEntries(entries, sortKey, sortDir);
  }, [children, showsFile, currentFile, sortKey, sortDir]);

  const sortedSearchEntries = useMemo(() => {
    const entries = (searchResults || []).map((hit) => {
      if (hit.kind === 'folder') {
        return {
          kind: 'folder',
          id: hit.folder.id,
          name: hit.folder.name || '',
          modifiedAt: hit.folder.updatedAt || hit.folder.createdAt || null,
          typeLabel: '파일 폴더',
          sizeBytes: 0,
          hasComparison: false,
          path: hit.path,
          folder: hit.folder,
        };
      }
      return {
        kind: 'file',
        id: hit.file.id,
        name: hit.file.fileName || '',
        modifiedAt: hit.file.uploadedAt || null,
        typeLabel: formatFileType(hit.file),
        sizeBytes: Number(hit.file.size) || 0,
        hasComparison: Boolean(hit.file.comparisonFile?.documentPath),
        path: hit.path,
        folder: hit.folder,
        file: hit.file,
      };
    });
    return sortExplorerEntries(entries, sortKey, sortDir);
  }, [searchResults, sortKey, sortDir]);

  useEffect(() => {
    setSelectedKeys(new Set());
    setAnchorKey(null);
    setSortKey('name');
    setSortDir('asc');
    if (!isSearchControlled) setInternalSearch('');
  }, [folder?.id, isSearchControlled]);

  useEffect(() => {
    if (selectedFileId) {
      const key = `file-${selectedFileId}`;
      setSelectedKeys(new Set([key]));
      setAnchorKey(key);
    }
  }, [selectedFileId]);

  const visibleEntries = isSearching ? sortedSearchEntries : sortedFolderEntries;
  const visibleKeys = useMemo(
    () => visibleEntries.map(entryKeyOf),
    [visibleEntries],
  );
  const prevVisibleKeysRef = useRef(visibleKeys);

  useEffect(() => {
    const prev = new Set(prevVisibleKeysRef.current);
    prevVisibleKeysRef.current = visibleKeys;
    setSelectedKeys((old) => {
      const next = new Set();
      old.forEach((k) => {
        if (!prev.has(k) || visibleKeys.includes(k)) next.add(k);
      });
      if (next.size === old.size) return old;
      return next;
    });
  }, [visibleKeys]);

  const selectOnly = (key) => {
    setSelectedKeys(new Set([key]));
    setAnchorKey(key);
  };

  const toggleSelect = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setAnchorKey(key);
  };

  const rangeSelect = (key) => {
    const a = anchorKey ? visibleKeys.indexOf(anchorKey) : -1;
    const b = visibleKeys.indexOf(key);
    if (a < 0 || b < 0) {
      selectOnly(key);
      return;
    }
    const [lo, hi] = a < b ? [a, b] : [b, a];
    setSelectedKeys(new Set(visibleKeys.slice(lo, hi + 1)));
  };

  const handleEntryClick = (e, entry) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    e.stopPropagation();
    focusPane();
    const key = entryKeyOf(entry);
    if (e.shiftKey) rangeSelect(key);
    else if (e.ctrlKey || e.metaKey) toggleSelect(key);
    else selectOnly(key);

    if (entry.kind === 'file') {
      if (!e.shiftKey && !e.ctrlKey && !e.metaKey && isSearching && entry.folder) {
        onNavigate?.(entry.folder.id);
      }
      onSelectFile?.(entry.file.id);
    } else {
      onSelectFile?.(null);
    }
  };

  const selectedFolderIds = () => (
    [...selectedKeysRef.current]
      .filter((k) => k.startsWith('folder-'))
      .map((k) => k.slice('folder-'.length))
      .filter((id) => {
        const f = folderMap?.[id];
        return f && !subMatFolderActions.isSystemRootFolder(f);
      })
  );

  const selectedFileInfos = () => (
    [...selectedKeysRef.current]
      .filter((k) => k.startsWith('file-'))
      .map((k) => k.slice('file-'.length))
      .map((id) => {
        const entry = visibleEntries.find((e) => e.kind === 'file' && String(e.id) === String(id));
        if (!entry?.file) return null;
        const folderId = entry.folder?.id || entry.file.folderId || folder?.id;
        return folderId ? { folderId, file: entry.file } : null;
      })
      .filter(Boolean)
  );

  if (selectedFolderIdsRef) {
    selectedFolderIdsRef.current = selectedFolderIds();
  }
  if (selectedFileInfosRef) {
    selectedFileInfosRef.current = selectedFileInfos();
  }

  useEffect(() => {
    onSelectionChange?.(selectedFolderIds().length + selectedFileInfos().length);
  }, [selectedKeys, visibleEntries]);

  const hasDeletableSelection = selectedFolderIds().length + selectedFileInfos().length > 0;

  const handleExplorerKeyDown = (e) => {
    if (busy) return;
    if (e.key !== 'Delete') return;
    if (e.nativeEvent?.isComposing) return;
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
    if (!selectedKeysRef.current.size) return;
    e.preventDefault();
    onDeleteSelection?.();
  };

  const clearFolderDropTarget = () => setFolderDropTargetId(null);
  const activeDropTargetId = folderDropTargetId || externalDropTargetId;

  const handleFolderDragStart = (e, f) => {
    if (busy) {
      e.preventDefault();
      return;
    }
    if (subMatFolderActions.isSystemRootFolder(f)) {
      e.preventDefault();
      return;
    }
    const key = `folder-${f.id}`;
    let ids;
    if (selectedKeysRef.current.has(key) && selectedKeysRef.current.size > 1) {
      ids = selectedFolderIds();
      if (!ids.includes(f.id)) ids = [f.id, ...ids];
    } else {
      selectOnly(key);
      onSelectFile?.(null);
      ids = [f.id];
    }
    if (!ids.length) {
      e.preventDefault();
      return;
    }
    subMatFolderActions.setFolderDragData(e.dataTransfer, ids);
  };

  const handleFolderDragEnd = () => {
    subMatFolderActions.endFolderDrag();
    clearFolderDropTarget();
    suppressClickRef.current = true;
  };

  const handleFolderRowDragOver = (e, f) => {
    if (busy) return;
    if (!subMatFolderActions.isFolderDrag(e.dataTransfer)) return;
    const dragging = subMatFolderActions.getActiveFolderDragIds();
    if (dragging.includes(f.id)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = e.ctrlKey ? 'copy' : 'move';
    setFolderDropTargetId(f.id);
    setDragOver(false);
  };

  const handleFolderRowDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setFolderDropTargetId((prev) => (prev === 'pane' ? prev : null));
  };

  const handleFolderRowDrop = (e, f) => {
    if (busy) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const sourceIds = subMatFolderActions.getFolderDragIds(e.dataTransfer);
    if (!sourceIds.length) return;
    e.preventDefault();
    e.stopPropagation();
    clearFolderDropTarget();
    subMatFolderActions.endFolderDrag();
    onFolderDrop?.(sourceIds, f, e.ctrlKey);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearFolderDropTarget();
    setDragOver(false);
    if (busy) return;

    const sourceIds = subMatFolderActions.getFolderDragIds(e.dataTransfer);
    if (sourceIds.length) {
      subMatFolderActions.endFolderDrag();
      if (folder && !folder.linkView) onFolderDrop?.(sourceIds, folder, e.ctrlKey);
      return;
    }

    if (!uploadable || !folder) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onUploadFile?.(file);
  };

  const handleDragOver = (e) => {
    if (busy) return;
    if (subMatFolderActions.isFolderDrag(e.dataTransfer)) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = e.ctrlKey ? 'copy' : 'move';
      setFolderDropTargetId('pane');
      setDragOver(false);
      return;
    }
    if (!uploadable) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
    clearFolderDropTarget();
  };

  // 빈 영역 드래그 = 마퀴 멀티선택
  useEffect(() => {
    const onMove = (e) => {
      const st = marqueeRef.current;
      if (!st) return;
      window.getSelection()?.removeAllRanges();
      st.x1 = e.clientX;
      st.y1 = e.clientY;
      const left = Math.min(st.x0, st.x1);
      const top = Math.min(st.y0, st.y1);
      const width = Math.abs(st.x1 - st.x0);
      const height = Math.abs(st.y1 - st.y0);
      setMarquee({ left, top, width, height });

      if (Math.hypot(st.x1 - st.x0, st.y1 - st.y0) < MARQUEE_THRESHOLD_PX) return;

      const box = {
        left,
        top,
        right: left + width,
        bottom: top + height,
      };
      const hit = new Set(st.base);
      document.querySelectorAll('[data-explorer-row]').forEach((el) => {
        const key = el.getAttribute('data-explorer-row');
        if (!key || !visibleKeys.includes(key)) return;
        const r = el.getBoundingClientRect();
        if (rectsIntersect(box, r)) hit.add(key);
      });
      setSelectedKeys(hit);
      if (hit.size) {
        const last = [...hit].pop();
        setAnchorKey(last);
      }
    };

    const onUp = () => {
      const st = marqueeRef.current;
      if (!st) return;
      const dist = Math.hypot(st.x1 - st.x0, st.y1 - st.y0);
      marqueeRef.current = null;
      document.body.style.userSelect = '';
      window.getSelection()?.removeAllRanges();
      setMarquee(null);
      if (dist >= MARQUEE_THRESHOLD_PX) {
        suppressClickRef.current = true;
        onSelectFile?.(null);
      } else if (!st.additive) {
        setSelectedKeys(new Set());
        setAnchorKey(null);
        onSelectFile?.(null);
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [visibleKeys, onSelectFile]);

  const handlePanePointerDown = (e) => {
    if (e.button !== 0) return;
    if (e.target?.closest?.('[data-explorer-row]')) return;
    if (e.target?.closest?.('[data-explorer-header]')) return;
    if (e.target?.closest?.('button, label, input, a')) return;
    e.preventDefault();
    focusPane();
    document.body.style.userSelect = 'none';
    window.getSelection()?.removeAllRanges();
    const additive = e.ctrlKey || e.metaKey;
    marqueeRef.current = {
      x0: e.clientX,
      y0: e.clientY,
      x1: e.clientX,
      y1: e.clientY,
      additive,
      base: additive ? new Set(selectedKeysRef.current) : new Set(),
    };
    setMarquee({
      left: e.clientX,
      top: e.clientY,
      width: 0,
      height: 0,
    });
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onUploadFile?.(file);
    e.target.value = '';
  };

  const handlePaneContextMenu = (e) => {
    if (isSearching || !folder) return;
    onContextMenu?.(e, { kind: 'pane', folder });
  };

  const isEmpty = children.length === 0 && !currentFile;

  if (!folder) {
    return (
      <Box sx={{ p: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', position: 'relative' }}>
        <Typography variant="body2" color="text.secondary">
          폴더를 선택하세요.
        </Typography>
        <SubMatWorkProgressOverlay progress={workProgress} />
      </Box>
    );
  }

  return (
    <Box
      ref={paneFocusRef}
      tabIndex={0}
      onKeyDown={handleExplorerKeyDown}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: chrome ? 440 : 0,
        outline: 'none',
        position: 'relative',
      }}
    >
      {chrome && (
        <SubMatFolderExplorerChrome
          folder={folder}
          folderMap={folderMap}
          uploading={uploading}
          activityLabel={activityLabel}
          busy={busy}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onNavigate={onNavigate}
          onCreateFolder={onCreateFolder}
          onDeleteFolder={onDeleteFolder}
          hasDeletableSelection={hasDeletableSelection}
          onUploadFile={onUploadFile}
          canGoBack={canGoBack}
          canGoForward={canGoForward}
          canGoUp={canGoUp}
          onGoBack={onGoBack}
          onGoForward={onGoForward}
          onGoUp={onGoUp}
          onRefresh={onRefresh}
          specOnly={specOnly}
          onSpecOnlyChange={onSpecOnlyChange}
        />
      )}

      {!chrome && isSearching && (
        <Box sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            「{searchQuery.trim()}」 검색 결과 · {searchResults.length}건
          </Typography>
        </Box>
      )}

      {uploading && <LinearProgress />}

      <Box
        data-submat-ctx=""
        data-submat-explorer=""
        {...(folder?.id ? { [subMatFolderActions.DROP_FOLDER_ATTR]: folder.id } : {})}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          outline: (() => {
            if (isSearching) return 'none';
            if (dragOver) return '2px dashed';
            if (activeDropTargetId === 'pane') return '1px dashed';
            if (folder && activeDropTargetId === folder.id && !folderDropTargetId) return '1px dashed';
            return 'none';
          })(),
          outlineColor: 'primary.main',
          outlineOffset: -2,
          bgcolor: (theme) => {
            if (isSearching) return 'transparent';
            if (dragOver) return selectionBg(theme);
            if (activeDropTargetId === 'pane') return selectionBg(theme);
            if (folder && activeDropTargetId === folder.id && !folderDropTargetId) return selectionBg(theme);
            return 'transparent';
          },
        }}
        onDragOver={isSearching ? undefined : handleDragOver}
        onDragLeave={isSearching ? undefined : handleDragLeave}
        onDrop={isSearching ? undefined : handleDrop}
        onPointerDown={handlePanePointerDown}
        onContextMenu={handlePaneContextMenu}
      >
        {marquee && marquee.width + marquee.height > 0 && (
          <Box
            sx={{
              position: 'fixed',
              left: marquee.left,
              top: marquee.top,
              width: marquee.width,
              height: marquee.height,
              border: '1px solid',
              borderColor: 'primary.main',
              bgcolor: (theme) => selectionBg(theme),
              pointerEvents: 'none',
              zIndex: (t) => t.zIndex.tooltip,
            }}
          />
        )}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            userSelect: 'none',
          }}
        >
          {isSearching ? (
            searchResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                일치하는 폴더·사용중 문서가 없습니다.
              </Typography>
            ) : (
              <>
                <Box
                  data-explorer-header=""
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    width: 'max-content',
                    minWidth: '100%',
                  }}
                >
                  <ExplorerHeader
                    widths={widths}
                    startResize={startResize}
                    onAutoFit={fitColumnToContent}
                    showComparison={showComparison}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                </Box>
                {sortedSearchEntries.map((entry) => {
                  if (entry.kind === 'folder') {
                    const key = entryKeyOf(entry);
                    const canDrag = !subMatFolderActions.isSystemRootFolder(entry.folder);
                    return (
                      <ExplorerRow
                        key={`search-folder-${entry.id}`}
                        entryKey={key}
                        widths={widths}
                        showComparison={showComparison}
                        icon={<SubMatFolderIcon folder={entry.folder} />}
                        primary={<HighlightedName text={entry.name} pattern={searchQuery} />}
                        modified={dateFormat(entry.modifiedAt)}
                        typeLabel={entry.typeLabel}
                        sizeLabel=""
                        title={entry.path}
                        selected={selectedKeys.has(key)}
                        dropTarget={activeDropTargetId === entry.folder.id}
                        dropFolderId={entry.folder.id}
                        draggable={canDrag}
                        cut={cutFolderIds.includes(entry.folder.id)}
                        onDragStart={(e) => handleFolderDragStart(e, entry.folder)}
                        onDragEnd={handleFolderDragEnd}
                        onDragOver={(e) => handleFolderRowDragOver(e, entry.folder)}
                        onDragLeave={handleFolderRowDragLeave}
                        onDrop={(e) => handleFolderRowDrop(e, entry.folder)}
                        onClick={(e) => handleEntryClick(e, entry)}
                        onDoubleClick={() => {
                          setSearchQuery('');
                          onNavigate(entry.folder.id);
                        }}
                        onContextMenu={(e) => onContextMenu(e, { kind: 'folder', folder: entry.folder })}
                      />
                    );
                  }
                  const key = entryKeyOf(entry);
                  return (
                    <ExplorerRow
                      key={`search-file-${entry.id}`}
                      entryKey={key}
                      widths={widths}
                      showComparison={showComparison}
                      icon={<span>📄</span>}
                      primary={<HighlightedName text={entry.name} pattern={searchQuery} />}
                      expiryDateLabel={formatSubMatEndDate(entry.file)}
                      expiryStatusLabel={formatSubMatExpiryStatusText(entry.file)}
                      expiryStatusColor={getSubMatExpiryStatusTextColor(entry.file)}
                      modified={dateFormat(entry.modifiedAt)}
                      typeLabel={entry.typeLabel}
                      sizeLabel={formatFileSize(entry.sizeBytes)}
                      comparisonSlot={(
                        <ComparisonViewButton
                          file={entry.file}
                          onPreviewFile={onPreviewFile}
                          onError={onError}
                        />
                      )}
                      title={entry.path}
                      plain
                      selected={selectedKeys.has(key)}
                      onClick={(e) => handleEntryClick(e, entry)}
                      onDoubleClick={async () => {
                        try {
                          await onPreviewFile?.(entry.file);
                        } catch (err) {
                          onError?.(err.message || '파일을 열 수 없습니다.');
                        }
                      }}
                      onContextMenu={(e) => onContextMenu(e, {
                        kind: 'file',
                        folder: entry.folder,
                        file: entry.file,
                      })}
                    />
                  );
                })}
              </>
            )
          ) : (
            <>
              {!uploadable && !folder?.linkView && children.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  시스템 루트에는 파일을 넣을 수 없습니다. 하위 폴더를 열어 주세요.
                </Typography>
              )}
              {folder?.linkView && isEmpty && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  파일이 없습니다. 파일은 실물 폴더에서 올립니다.
                </Typography>
              )}

              {(children.length > 0 || currentFile) && (
                <Box
                  data-explorer-header=""
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    width: 'max-content',
                    minWidth: '100%',
                  }}
                >
                  <ExplorerHeader
                    widths={widths}
                    startResize={startResize}
                    onAutoFit={fitColumnToContent}
                    showComparison={showComparison}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                </Box>
              )}

              {sortedFolderEntries.map((entry) => {
                if (entry.kind === 'folder') {
                  const key = entryKeyOf(entry);
                  const isLink = Boolean(entry.folder.linkView);
                  const canDrag = !isLink && !subMatFolderActions.isSystemRootFolder(entry.folder);
                  const linkTargets = isLink
                    ? []
                    : subMatFolderActions.linkTargetsOf(entry.folder.id, allFolders || folders, links);
                  return (
                    <ExplorerRow
                      key={entry.id}
                      entryKey={key}
                      widths={widths}
                      showComparison={showComparison}
                      icon={<SubMatFolderIcon folder={entry.folder} />}
                      primary={entry.name}
                      endAdornment={isLink ? (
                        <AdditionalDocBadge
                          title={`실물 폴더는 ${entry.folder.name}입니다. 여기서는 보기와 다운로드만 됩니다.`}
                        />
                      ) : (linkTargets.length > 0 ? (
                        <AdditionalDocBadge
                          filled
                          title={`${linkTargets.map((target) => target.name).filter(Boolean).join(', ')}에 붙어 있습니다.`}
                          onClick={() => onOpenAdditional?.(entry.folder)}
                        />
                      ) : null)}
                      modified={dateFormat(entry.modifiedAt)}
                      typeLabel={entry.typeLabel}
                      sizeLabel=""
                      selected={selectedKeys.has(key)}
                      dropTarget={!isLink && activeDropTargetId === entry.folder.id}
                      dropFolderId={isLink ? undefined : entry.folder.id}
                      draggable={canDrag}
                      cut={cutFolderIds.includes(entry.folder.id)}
                      onDragStart={canDrag ? (e) => handleFolderDragStart(e, entry.folder) : undefined}
                      onDragEnd={handleFolderDragEnd}
                      onDragOver={isLink ? undefined : (e) => handleFolderRowDragOver(e, entry.folder)}
                      onDragLeave={handleFolderRowDragLeave}
                      onDrop={isLink ? undefined : (e) => handleFolderRowDrop(e, entry.folder)}
                      onClick={(e) => handleEntryClick(e, entry)}
                      onDoubleClick={() => onNavigate(entry.folder.id)}
                      onContextMenu={(e) => onContextMenu(e, { kind: 'folder', folder: entry.folder })}
                    />
                  );
                }
                const key = entryKeyOf(entry);
                return (
                  <ExplorerRow
                    key={`file-${entry.id}`}
                    entryKey={key}
                    widths={widths}
                    showComparison={showComparison}
                    icon={<span>📄</span>}
                    primary={entry.name}
                    expiryDateLabel={formatSubMatEndDate(entry.file)}
                    expiryStatusLabel={formatSubMatExpiryStatusText(entry.file)}
                    expiryStatusColor={getSubMatExpiryStatusTextColor(entry.file)}
                    modified={dateFormat(entry.modifiedAt)}
                    typeLabel={entry.typeLabel}
                    sizeLabel={formatFileSize(entry.sizeBytes)}
                    comparisonSlot={(
                      <ComparisonViewButton
                        file={entry.file}
                        onPreviewFile={onPreviewFile}
                        onError={onError}
                      />
                    )}
                    plain
                    selected={selectedKeys.has(key)}
                    onClick={(e) => handleEntryClick(e, entry)}
                    onDoubleClick={async () => {
                      try {
                        await onPreviewFile?.(entry.file);
                      } catch (err) {
                        onError?.(err.message || '파일을 열 수 없습니다.');
                      }
                    }}
                    onContextMenu={(e) => onContextMenu(e, {
                      kind: 'file',
                      folder,
                      file: entry.file,
                    })}
                  />
                );
              })}

              {uploadable && !isEmpty && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 72,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    minWidth: '100%',
                    boxSizing: 'border-box',
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color={dragOver ? 'primary' : 'text.secondary'}
                    sx={{
                      textAlign: 'center',
                      opacity: dragOver ? 1 : 0.55,
                      fontWeight: dragOver ? 600 : 400,
                    }}
                  >
                    PDF를 여기로 끌어다 놓아 업로드할 수 있습니다.
                  </Typography>
                </Box>
              )}

              {uploadable && isEmpty && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    이 폴더는 비어 있습니다.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    PDF를 끌어다 놓거나, 우클릭·툴바에서 업로드·하위 폴더를 추가하세요.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {canAddChild && (
                      <Button size="small" startIcon={<Add />} onClick={onCreateFolder}>
                        하위 폴더
                      </Button>
                    )}
                    <Button size="small" component="label" startIcon={<CloudUpload />} disabled={uploading}>
                      PDF 업로드
                      <input type="file" hidden accept={PDF_ACCEPT} onChange={handleInputChange} />
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      <SubMatWorkProgressOverlay progress={workProgress} />
    </Box>
  );
};

export default SubMatFolderExplorerPane;
